import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { API_BASE_URL } from '../utils/config';
import { notifySuccess, notifyError } from '../helper/notify';

const WP_API = `${API_BASE_URL}/api/wordpress`;
const SHOPIFY_API = `${API_BASE_URL}/api/shopify`;

// What a finished Shopify add says, like the WordPress add: a line for each store that was worked on, one for the whole
// add, a note when a product could not be put into the collection that was asked for, and the last reasons products failed.
// A product that was already in the store is skipped (it stays as it is) and is said apart from the ones that were added.
const alreadyIn = (n) => `${n} already in the store (skipped)`;
function announceShopifyEnd(j) {
    for (const s of j.stores || []) {
        if (!(s.current > 0)) continue;
        const parts = [`${s.added || 0} added`];
        if (s.skipped > 0) parts.push(alreadyIn(s.skipped));
        if (s.updated > 0) parts.push(`${s.updated} updated`); // an add from before products already in the store were skipped
        parts.push(`${s.failed || 0} failed`);
        const msg = `${s.store_name}: ${parts.join(', ')}`;
        (s.failed || 0) > 0 ? notifyError(msg) : notifySuccess(msg);
    }
    const skipped = j.skipped > 0 ? `, ${alreadyIn(j.skipped)}` : '';
    if (j.status === 'cancelled') {
        notifyError(`Cancelled — ${j.success} added, ${j.failed} failed${skipped}`);
    } else {
        let msg;
        if (j.failed === 0 && j.success === 0 && j.skipped > 0) {
            msg = `Nothing new to add: ${j.skipped === 1 ? 'the product was' : `all ${j.skipped} products were`} already in the store.`;
        } else if (j.failed === 0) {
            msg = `${j.success} product${j.success > 1 ? 's' : ''} added successfully${skipped}`;
        } else {
            msg = `${j.success} added, ${j.failed} failed${skipped}`;
        }
        j.failed > 0 ? notifyError(msg) : notifySuccess(msg);
    }
    if (j.warnings > 0) {
        notifyError(`${j.warnings} product${j.warnings > 1 ? 's were' : ' was'} not put into the collection you chose.`);
    }
    if (j.unpublished > 0) {
        notifyError(`${j.unpublished} product${j.unpublished > 1 ? 's were' : ' was'} not published to the Online Store and Point of Sale. If this keeps happening, approve the new permissions for the store under Integrations.`);
    }
    [...new Set(j.errors || [])].slice(-2).forEach((m) => notifyError(m));
}

// Lives above <Routes> (see App.jsx) so it survives navigating between admin
// pages — only AdminLayout (and everything inside it) remounts per route,
// this provider does not. Combined with the backend BulkJob model resuming
// itself after a server restart, the only thing that can stop a bulk
// add/remove job is the explicit Cancel button.
const BulkJobContext = createContext(null);

export const BulkJobProvider = ({ children }) => {
    const { token: authToken } = useSelector(s => s.auth);
    const [job, setJob] = useState(null); // { _id, type, status, total, current, success, failed }
    const pollRef = useRef(null);
    // v4.65.0: 'add' jobs now work through several stores at once (bounded
    // concurrency — see processBulkJob), so ONE store finishing is no longer
    // the same moment as the whole job finishing. This tracks which stores
    // have already had their own completion toast fired, so each store gets
    // notified exactly once — the moment ITS OWN response comes back — not
    // once at the very end for the whole job. Keyed by job id + store id so
    // a second job right after the first doesn't inherit stale entries.
    const notifiedStoresRef = useRef(new Set());

    const stopPolling = useCallback(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, []);

    const poll = useCallback((jobId, token) => {
        stopPolling();
        pollRef.current = setInterval(async () => {
            try {
                const res = await axios.get(`${WP_API}/products/bulk-jobs/${jobId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const j = res.data.job;
                if (!j) { stopPolling(); setJob(null); return; }
                setJob(j);

                // Per-store completion — 'add' only (added/updated are only
                // ever tracked for 'add' jobs; 'remove' doesn't have that
                // distinction, and keeps its existing single end-of-job
                // message below instead).
                if (j.type === 'add' && Array.isArray(j.stores)) {
                    for (const s of j.stores) {
                        const total = s.total || 0;
                        const doneKey = `${jobId}:${s.store_id}`;
                        if (total > 0 && (s.current || 0) >= total && !notifiedStoresRef.current.has(doneKey)) {
                            notifiedStoresRef.current.add(doneKey);
                            const name = s.store_name || 'Store';
                            const msg = `${name}: ${s.added || 0} added, ${s.updated || 0} updated, ${s.failed || 0} failed`;
                            (s.failed || 0) > 0 ? notifyError(msg) : notifySuccess(msg);
                        }
                    }
                }

                if (j.status !== 'running') {
                    stopPolling();
                    const verb = j.type === 'add' ? 'added' : 'removed';
                    if (j.status === 'cancelled') {
                        notifyError(`Cancelled — ${j.success} ${verb}, ${j.failed} failed`);
                    } else {
                        const msg = j.failed === 0
                            ? `${j.success} product${j.success > 1 ? 's' : ''} ${verb} successfully`
                            : `${j.success} ${verb}, ${j.failed} failed`;
                        j.failed > 0 ? notifyError(msg) : notifySuccess(msg);
                    }
                }
            } catch (e) {
                console.error('[bulk-job] poll error:', e.message);
            }
        }, 1200);
    }, [stopPolling]);

    // Add to Shopify. Like the WordPress add above it is a job that runs on the backend (shopify/services/pushJobs.js):
    // it goes on through a page refresh, a closed tab or a backend restart, and only Cancel stops it. This just follows
    // it — polls its progress for the banner, says how it ended, and picks it up again after a refresh. Kept in its own
    // state, with its own poller, because a WordPress add can be running at the same time.
    const [shopifyJob, setShopifyJob] = useState(null); // the job as the server reports it (platform: 'shopify')
    const shopifyPollRef = useRef(null);

    const stopShopifyPolling = useCallback(() => {
        if (shopifyPollRef.current) { clearInterval(shopifyPollRef.current); shopifyPollRef.current = null; }
    }, []);

    const pollShopify = useCallback((jobId, token) => {
        stopShopifyPolling();
        shopifyPollRef.current = setInterval(async () => {
            try {
                const res = await axios.get(`${SHOPIFY_API}/push-jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
                const j = res.data.job;
                if (!j) { stopShopifyPolling(); setShopifyJob(null); return; }
                setShopifyJob(j);
                if (j.status !== 'running') { stopShopifyPolling(); announceShopifyEnd(j); }
            } catch (e) {
                if (e.response && e.response.status === 404) { stopShopifyPolling(); setShopifyJob(null); return; }
                console.error('[shopify-push-job] poll error:', e.message); // a network blip — keep polling
            }
        }, 1200);
    }, [stopShopifyPolling]);

    // Called with the job the server just created (POST /push-jobs), or the one still running after a refresh.
    const startShopifyJob = useCallback((j) => {
        setShopifyJob(j);
        if (authToken) pollShopify(j._id, authToken);
    }, [pollShopify, authToken]);

    const cancelShopifyJob = useCallback(async () => {
        if (!shopifyJob?._id || !authToken) return;
        try {
            await axios.post(`${SHOPIFY_API}/push-jobs/${shopifyJob._id}/cancel`, {}, { headers: { Authorization: `Bearer ${authToken}` } });
        } catch (e) { console.error('[shopify-push-job] cancel failed:', e.message); }
    }, [shopifyJob, authToken]);

    useEffect(() => {
        if (!authToken) { stopShopifyPolling(); setShopifyJob(null); return undefined; }
        axios.get(`${SHOPIFY_API}/push-jobs/active`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then((res) => { if (res.data.job) startShopifyJob(res.data.job); })
            .catch(() => {});
        return stopShopifyPolling;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    const startJob = useCallback((j) => {
        notifiedStoresRef.current = new Set();
        setJob(j);
        if (authToken) poll(j._id, authToken);
    }, [poll, authToken]);

    const cancelJob = useCallback(async () => {
        if (!job?._id || !authToken) return;
        try {
            await axios.post(`${WP_API}/products/bulk-jobs/${job._id}/cancel`, {}, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
        } catch (e) { console.error('[bulk-job] cancel failed:', e.message); }
    }, [job, authToken]);

    // Resume an in-progress job on login/refresh — the backend job itself
    // never stopped (it keeps running/resumes server-side even through a
    // backend restart), this just re-attaches the UI to it.
    useEffect(() => {
        if (!authToken) { stopPolling(); setJob(null); return; }
        axios.get(`${WP_API}/products/bulk-jobs/active`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then(res => { if (res.data.job) startJob(res.data.job); })
            .catch(() => {});
        return stopPolling;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    return (
        <BulkJobContext.Provider value={{ job, startJob, cancelJob, shopifyJob, startShopifyJob, cancelShopifyJob }}>
            {children}
        </BulkJobContext.Provider>
    );
};

export const useBulkJob = () => useContext(BulkJobContext);
